import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./requestAccess.css";
import ReactHeader from "./reactHeader";
import FooterSection from "./footerSection";
import AnimatedBlock from "./animatedBlock";
import emailjs from '@emailjs/browser';
import { msalInstance } from "./msalInstance";
import { decodeAccessCode } from "./accessCode";

const RequestAccessSection = () => {
    const { accessCode: accessCodeFromUrl } = useParams();
    const location = useLocation();
    const linkAccessCode = location.state?.accessCode;

    const [formData, setFormData] = useState({
        email: "",
        accessCode: accessCodeFromUrl ? decodeURIComponent(accessCodeFromUrl) : "",
    });

    const [status, setStatus] = useState(null); // null | "success" | "invalid" | "error"
    const [loading, setLoading] = useState(false);

    // If the visitor is already signed in via B2C (see src/auth.js), prefill
    // from their account claims. Fields stay editable; nothing changes for
    // visitors who aren't signed in.
    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                await msalInstance.initialize();
            } catch (err) {
                console.error("MSAL initialize failed:", err);
                return;
            }
            if (cancelled) return;

            const claims = msalInstance.getActiveAccount()?.idTokenClaims;
            if (!claims && !linkAccessCode) return;

            setFormData((prev) => ({
                email: prev.email || claims?.email || "",
                accessCode: prev.accessCode || linkAccessCode || "",
            }));
        })();

        return () => {
            cancelled = true;
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const isFormFilled = formData.email.trim() !== "" && formData.accessCode.trim() !== "";

    const handleSubmit = async (e) => {
        e.preventDefault();

        const decoded = decodeAccessCode(formData.accessCode.trim());
        if (!decoded) {
            setStatus("invalid");
            return;
        }

        setLoading(true);
        setStatus(null);

        try {
            await emailjs.send(
                'service_3fxzp8s',
                'template_wkvu5m9',
                {
                    user_email:   formData.email,
                    access_code:  formData.accessCode,
                    requested_by: decoded.email,
                    requested_at: decoded.timestamp,
                },
                { publicKey: 'Nx44CgjpAjr6nxQmc' }
            );

            setStatus("success");
            setFormData({ email: "", accessCode: "" });
        } catch (err) {
            console.error("EmailJS error:", err);
            setStatus("error");
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setStatus(null);
        setFormData({ email: "", accessCode: "" });
    };

    if (status === "success") {
        return (
            <section className="page-section section-style section-style-rta">
                <AnimatedBlock>
                    <div className="rta-content rta-success-content">
                        <div className="rta-success-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="9 12 11 14 15 10" />
                            </svg>
                        </div>
                        <h1 className="rta-success-heading">Request Received</h1>
                        <p className="rta-success-body">
                            Your access request has been submitted for approval.
                        </p>

                        <button className="rta-status-btn rta-success-reset" onClick={resetForm}>Submit Another Request</button>
                    </div>
                </AnimatedBlock>
            </section>
        );
    }

    return (
        <section className="page-section section-style section-style-rta">
            <div className="rta-content rta-two-column">

                {/* LEFT COLUMN */}
                <AnimatedBlock>
                    <div className="rta-column rta-left-column">
                        <h1>Request Access</h1>
                        <p>
                            <strong>Need a code?</strong><br />
                            <a href="/contact">
                                Contact us for access
                            </a>
                        </p>
                    </div>
                </AnimatedBlock>

                {/* RIGHT COLUMN */}
                <div className="rta-column rta-right-column">

                    {status === "invalid" && (
                        <div className="rta-message-container">
                            <div className="rta-status-card rta-status-card--error">
                                <div className="rta-status-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                </div>
                                <h2 className="rta-status-heading">Invalid Access Code</h2>
                                <p className="rta-status-body">
                                    The code you entered is not valid. Please double-check your code and try again, or{" "}
                                    <a href="mailto:support@khordeveloper.com">contact us</a> for assistance.
                                </p>
                                <button className="rta-status-btn" onClick={resetForm}>Try Again</button>
                            </div>
                        </div>
                    )}

                    {status === "error" && (
                        <div className="rta-message-container">
                            <div className="rta-status-card rta-status-card--error">
                                <div className="rta-status-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                </div>
                                <h2 className="rta-status-heading">Submission Failed</h2>
                                <p className="rta-status-body">
                                    We were unable to process your request. Please email us directly at{" "}
                                    <a href="mailto:support@khordeveloper.com">support@khordeveloper.com</a>.
                                </p>
                                <button className="rta-status-btn" onClick={resetForm}>Try Again</button>
                            </div>
                        </div>
                    )}

                    {!status && (
                        <form className="rta-form" onSubmit={handleSubmit}>

                            {/* Email */}
                            <div className="rta-form-group">
                                <label>
                                    Email Address <span className="rta-required">*</span>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                    />
                                </label>
                            </div>

                            {/* Access Code */}
                            <div className="rta-form-group">
                                <label>
                                    Access Code <span className="rta-required">*</span>
                                    <input
                                        type="text"
                                        name="accessCode"
                                        placeholder="Enter your access code"
                                        required
                                        value={formData.accessCode}
                                        onChange={handleChange}
                                    />
                                </label>
                            </div>

                            {/* Submit */}
                            <div className="rta-form-group">
                                <button
                                    className="btn"
                                    type="submit"
                                    disabled={!isFormFilled || loading}
                                >
                                    {loading ? <div className="spinner" /> : "Request Access"}
                                </button>
                            </div>

                        </form>
                    )}
                </div>

            </div>
        </section>
    );
};

const RequestAccess = () => (
    <div className="App">
        <Helmet>
            <title>Request Temporary Access | Khor Developer</title>
            <meta name="description" content="Request temporary access to Khor Developer using your issued access code." />
            <meta property="og:title" content="Request Temporary Access | Khor Developer" />
            <meta property="og:url" content="https://www.khordeveloper.com/request-access" />
            <link rel="canonical" href="https://www.khordeveloper.com/request-access" />
        </Helmet>
        <ReactHeader />
        <RequestAccessSection />
        <FooterSection />
    </div>
);

export default RequestAccess;
